import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Eyebrow } from "@/components/Primitives";
import { ExperienceCard } from "@/components/ExperienceCard";
import ExperienceForm from "@/components/ExperienceForm";
import { getExperiences } from "@/lib/api";

export default function ClientExperiences() {
  const [experiences, setExperiences] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    getExperiences()
      .then((d) => {
        setExperiences(d.experiences || []);
        setDisclaimer(d.disclaimer || "");
      })
      .finally(() => setLoading(false));
  }, []);

  const openForm = () => {
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const featured = experiences.slice(0, 2);
  const rest = experiences.slice(2);

  return (
    <div className="pb-8">
      {/* Intro */}
      <Section className="pt-14 sm:pt-20">
        <div className="max-w-3xl">
          <Eyebrow>Client experiences</Eyebrow>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-stone-900 leading-[1.08] mt-5">
            Real experiences. Personal transformation.
          </h1>
          <div className="mt-6 space-y-4 text-lg text-stone-600 leading-relaxed">
            <p>Every person's experience with change is different.</p>
            <p>
              These are reflections from individuals who chose to explore their patterns, do the
              work, and pursue meaningful change through TENET.
            </p>
            <p className="text-base text-stone-500">
              Their experiences are personal to them and are not a promise or guarantee of specific
              results.
            </p>
          </div>
        </div>
      </Section>

      {/* Featured — two side by side */}
      <Section className="mt-12 sm:mt-16">
        {loading ? (
          <div className="grid gap-6 md:grid-cols-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-64 rounded-2xl border border-stone-200 bg-white/60 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 items-start" data-testid="featured-experiences">
              {featured.map((exp, i) => (
                <ExperienceCard key={exp.id} exp={exp} large index={i} />
              ))}
            </div>

            {rest.length > 0 && (
              <div className="mt-2 columns-1 md:columns-2 lg:columns-3 gap-6" data-testid="experience-grid">
                {rest.map((exp, i) => (
                  <ExperienceCard key={exp.id} exp={exp} index={i} />
                ))}
              </div>
            )}
          </>
        )}
      </Section>

      {/* CTA */}
      <Section className="mt-24 sm:mt-32">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#1F3D2E] p-10 sm:p-14 text-center">
          <div
            className="absolute inset-0 opacity-20"
            style={{ background: "radial-gradient(50% 70% at 85% 15%, rgba(212,175,90,0.5), transparent 60%)" }}
          />
          <div className="relative max-w-2xl mx-auto">
            <i className="fa-solid fa-tower-observation text-amber-300/80 text-2xl" />
            <h2 className="font-serif-display text-3xl sm:text-4xl text-amber-50 mt-4 leading-tight">
              Your experience may help someone else take their first step
            </h2>
            <p className="text-stone-300 mt-5 leading-relaxed">
              Making a meaningful change can feel uncertain — especially when someone has already
              tried several approaches. Sometimes hearing from another person who once felt skeptical,
              stuck, or unsure can make taking the next step feel possible.
            </p>
            <p className="text-stone-300 mt-3 leading-relaxed">
              If you have worked with TENET and would like to share your experience, we would be
              grateful to hear from you.
            </p>
            <button
              onClick={openForm}
              data-testid="share-experience-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-semibold text-stone-900 hover:bg-amber-400 transition-colors"
            >
              Share your experience <i className="fa-solid fa-feather text-xs" />
            </button>
          </div>
        </div>
      </Section>

      {/* Submission form */}
      {showForm && (
        <Section className="mt-16" >
          <div ref={formRef} className="max-w-2xl mx-auto rounded-[2rem] border border-stone-200 bg-white p-7 sm:p-10 scroll-mt-24">
            <ExperienceForm />
          </div>
        </Section>
      )}

      {/* Disclaimer */}
      <Section className="mt-20">
        <p className="max-w-3xl text-sm text-stone-500 leading-relaxed" data-testid="experiences-disclaimer">
          {disclaimer}
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/assessments/tenet-readiness"
            className="btn-terracotta rounded-full px-6 py-3.5 text-sm font-semibold"
          >
            Take the Transformation Readiness Assessment
          </Link>
          <Link
            to="/consultation"
            className="rounded-full px-6 py-3.5 text-sm font-semibold border border-stone-300 text-stone-800 hover:bg-white transition-colors"
          >
            Schedule a private consultation
          </Link>
        </div>
      </Section>
    </div>
  );
}
